<template>
  <div class="modal-manager">
    <!--
      Centralized Modal Manager

      Pattern: All modals are imported once here and listen to their own eventBus events.
      Each modal manages its own visibility via isVisible ref and eventBus listeners.

      To open a modal, emit the appropriate event:
        eventBus.emit('modal.cliente.open', { id: 123 })
        eventBus.emit('cliente.edit', { id: 123 })
        eventBus.emit('recebimento.open-modal-edit', { id: 456 })

      IMPORTANT: Do NOT import modals in parent components - they are all here.
    -->

    <!-- Domain Modals -->
    <ClienteModal />
    <ClienteArchiveModal />
    <ClienteVinculaModal />
    <ClienteComboCorrecaoModal />
    <ClienteNotificacaoModal />
    <OrcamentosModal />
    <OrcamentoServicoModal />
    <RecebimentoModal />
    <RecebimentoParceladoModal />
    <RecebimentosCsvImportModal />
    <DespesasModal />
    <DespesasCsvImportModal />
    <DespesasCategoriasModal />
    <DespesasStatusModal />
    <ChamadosModal />
    <CompromissoModal />
    <CompromissoServicoModal />
    <ContatoFidelizacaoModal />
    <ServicosModal />
    <ServicosCategoriasModal />
    <PDFElementsModal />
    <FormasPagamentoModal />
    <FormasPagamentoFranquiaModal />
    <FiscalConfigModal v-if="baasEnabled" />
    <InvoicesModal v-if="baasEnabled" />
    <OrigensModal />
    <SiteUnidadesModal />
    <AcoesMarketingModal />
    <SiteLeadsModal />
    <SiteTemplatesModal />
    <ContasModal />
    <FranquiasModal />
    <FranquiasWizard />
    <ConfigurationModal />
    <LocationsStatesModal />
    <LocationsCitiesModal />
    <LocationsNeighborhoodsModal />
    <LocationsSelectionModal />
    <DevicesFranquiasModal />
    <DeviceRegistrationModal />
    <InstagramAccountsModal />
    <DevicePickerModal />
    <UserModal />
    <ChangePasswordModal />
    <MensagensProgramadasLoteModal />
    <MessageTemplateSelectionModal />
    <WhatsappMessageTemplatesModal />
    <PaymentRulesModal />
    <PaymentRulesSummaryModal />
    <PayrollModal />
    <CreatePeriodModal />
    <PayrollRefundsModal />
    <PayrollRefundsDeclineModal />
    <EmployeeAdvancesModal />
    <EmployeeDeductionsModal />
    <DeductionsModal />
    <DeductionsListModal />
    <OccurrencesModal />
    <ImageViewerModal />
    <FiltroModal />
    <PlanoContasModal />
    <FechamentosModal />
    <TranslationsModal />
    <LanguagesModal />

    <!-- SO (Service Order) Domain Modals -->
    <SOServiceCategoriesModal />
    <SOProductCategoriesModal />
    <SOPrioritiesModal />
    <SOItemAttributesModal />
    <SOReportAttributesModal />
    <SOCatalogFiscalModal />
    <SOServicesModal />
    <SOServicePrioritiesModal />
    <SOAdditionalServicePricesModal />
    <SOCombosModal />
    <SOComboPricesModal />
    <SOStockAdjustModal />
    <SOStockMovementsModal />
    <SOStockCreateModal />
    <SOAdditionalServicesModal />
    <SOProductionStepsModal />
    <SOOrderItemDetailModal />
    <SODeliveryDateChangeModal />
    <SOReprocessoModal />
    <SOLaudosComparisonModal />
    <GlobalSearchModal />

    <!-- PDV Modals -->
    <PDVClienteWizardModal />
    <PDVServicoWizardModal />
    <PDVProdutosWizardModal />
    <PDVRegistrarEntregaModal />
    <PDVEntregasNotifyModal />
    <PDVEnviarWhatsAppModal />
    <PDVEntregasDeliveryModal />
    <PDVCheckoutModal />
    <PDVCancelarOSModal />
    <PDVVenderComboModal />
    <ForceOutOfOrderModal />
    <CaixaModal />
    <CofreModal />
    <RedeSituacaoModal />
    <RedeFichaModal />
    <RedeEncerramentoModal />
    <RedeEncerramentosModal />

    <!-- Franchises Subaccounts Modals -->
    <FranchisesSubaccountsWizard v-if="baasEnabled" />

    <!-- Payment Links Modal -->
    <PaymentLinksModal v-if="baasEnabled" />
    <PaymentLinksCreateWizard v-if="baasEnabled" />

    <!-- Anticipations Modal -->
    <AnticipationDetailModal v-if="baasEnabled" />

    <!-- Aniversariantes Modals -->
    <AniversariantePreviewModal />
    <AniversarianteLoteModal />

    <!-- Webhook Logs Modal -->
    <WebhookDetailModal />

    <!-- Agenda/Wizard Modals -->
    <GestaoCompromissoModal />
    <AgendaBloqueioModal />
    <CheckinWizardModal />
    <WizardServicosModal />
    <WizardAddServicoModal />
    <AgendaAddCompromissoWizard />
    <MensagensProgramadasWizard />
    <UserWizard />
    <OrcamentoAddServicoWizard />
    <RecebimentoWizard />
    <RefundWizard v-if="baasEnabled" />

    <!-- Support Ticket Modal -->
    <SupportTicketModal />
    <TicketDetailModal />

    <!-- LGPD Admin Modals -->
    <LgpdTermoModal />
    <LgpdAceiteDetalheModal />

    <!-- Assistant Sidebar — feature `assistente`; not mounted at all when off -->
    <AssistantSidebar v-if="assistenteEnabled" />
    <AssistantFranchiseModal v-if="assistenteEnabled" />

    <!-- Subscription Modals -->
    <SubscribeModal />
    <SubscriptionDetailModal />

    <!-- Pack Purchase Modal (franqueado-facing top-up) -->
    <PackPurchaseModal />

    <!-- Staff Subscription / Credits Modals -->
    <StaffSubscriptionPlanModal />
    <StaffSubscriptionsExtendModal />
    <StaffSweepRunsModal />
    <StaffCreditsTopupModal />
    <StaffCreditsSyncModal />
    <CreditTransactionModal />
    <CreditPackModal />
    <ManualAdjustmentModal />

    <!-- Staff Transactions Modal -->
    <StaffTransactionModal />

    <!-- Staff Grace Periods Modals -->
    <GraceGrantModal />
    <GraceRevokeModal />

    <!-- Scheduled Jobs (S10) — forced deviation, ScheduledJobFormModal.vue's
         ownership list does not include this file, but "Modals via eventBus
         ... never imported in parent components" (CLAUDE.md rule 8) requires
         every {Domain}Modal to be mounted here or its eventBus.on() never
         subscribes and 'Novo job'/'Editar' become inert — the same class of
         silent-inertia bug this whole S10 domain exists to prevent. -->
    <ScheduledJobFormModal />

    <!-- Franchisor Channels (S13) — same forced deviation as ScheduledJobFormModal
         above: mounted here so eventBus.on('modal.channelForm.open') actually
         subscribes. -->
    <ChannelFormModal />
    <SubscriptionTermModal />
    <SubscriptionTermAcceptanceModal />
    <ChannelTemplateModal />
    <TriggerBindingModal />

    <!-- Platform events console (/config/webhooks) — subscription create/edit and
         the test-send, both driven by eventBus ('modal.platformWebhooks.open',
         'modal.platformWebhooksTestSend.open'). -->
    <PlatformWebhooksModal />
    <PlatformWebhooksTestSendModal />

    <!-- Manual Reconciliation (#27 follow-up) — result viewer shared by both
         sync doors (subscription cycles + credit purchases). -->
    <ReconciliationResultModal />

    <!-- Billing 360 franchise detail (tabs live in the modal, list is the page). -->
    <BillingThreeSixtyModal />

    <!-- Setup Fee Modals -->
    <SetupFeeEmitModal />
    <SetupFeeMarketModal />
    <SetupFeeMarkPaidModal />
    <SetupFeeExemptModal />
    <SetupFeeRerequireModal />
    <PegOverrideModal />
    <SetupFeeDetailModal />


  </div>
</template>


<script setup lang="ts">
import { onMounted } from 'vue';
import { log } from '../utils/DebugLogger';

// Domain Modals
import ClienteModal from '../domains/cliente/ClienteModal.vue';
import ClienteArchiveModal from '../domains/cliente/ClienteArchiveModal.vue';
import ClienteVinculaModal from '../domains/cliente/ClienteVinculaModal.vue';
import ClienteComboCorrecaoModal from '../domains/cliente/ClienteComboCorrecaoModal.vue';
import ClienteNotificacaoModal from '../domains/cliente/ClienteNotificacaoModal.vue';
import OrcamentosModal from '../domains/orcamento/OrcamentosModal.vue';
import OrcamentoServicoModal from '../domains/orcamentoservico/OrcamentoServicoModal.vue';
import RecebimentoModal from '../domains/recebimento/RecebimentoModal.vue';
import RecebimentoParceladoModal from '../domains/recebimento/RecebimentoParceladoModal.vue';
import RecebimentosCsvImportModal from '../domains/recebimento/RecebimentosCsvImportModal.vue';
import DespesasModal from '../domains/despesas/DespesasModal.vue';
import DespesasCsvImportModal from '../domains/despesas/DespesasCsvImportModal.vue';
import DespesasCategoriasModal from '../domains/despesas_categorias/DespesasCategoriasModal.vue';
import DespesasStatusModal from '../domains/despesas_status/DespesasStatusModal.vue';
import ChamadosModal from '../domains/chamados/ChamadosModal.vue';
import CompromissoModal from '../domains/compromisso/CompromissoModal.vue';
import CompromissoServicoModal from '../domains/compromissoservico/CompromissoServicoModal.vue';
import ContatoFidelizacaoModal from '../domains/contato_fidelizacao/ContatoFidelizacaoModal.vue';
import ServicosModal from '../domains/servicos/ServicosModal.vue';
import ServicosCategoriasModal from '../domains/servicos_categorias/ServicosCategoriasModal.vue';
import PDFElementsModal from '../domains/pdf_elements/PDFElementsModal.vue';
import FormasPagamentoModal from '../domains/formas-pagamento/FormasPagamentoModal.vue';
import FormasPagamentoFranquiaModal from '../domains/formas-pagamento/FormasPagamentoFranquiaModal.vue';
import FiscalConfigModal from '../domains/subaccounts/fiscal-config/FiscalConfigModal.vue';
import InvoicesModal from '../domains/subaccounts/invoices/InvoicesModal.vue';
import OrigensModal from '../domains/origens/OrigensModal.vue';
import SiteUnidadesModal from '../domains/site-unidades/SiteUnidadesModal.vue';
import AcoesMarketingModal from '../domains/marketing/AcoesMarketingModal.vue';
import SiteLeadsModal from '../domains/site-leads/SiteLeadsModal.vue';
import SiteTemplatesModal from '../domains/site-templates/SiteTemplatesModal.vue';
import ContasModal from '../domains/contas/ContasModal.vue';
import FranquiasModal from '../domains/franquias/FranquiasModal.vue';
import FranquiasWizard from '../domains/franquias/FranquiasWizard.vue';
import ConfigurationModal from '../domains/franquias/ConfigurationModal.vue';
import LocationsStatesModal from '../domains/franquias/LocationsStatesModal.vue';
import LocationsCitiesModal from '../domains/franquias/LocationsCitiesModal.vue';
import LocationsNeighborhoodsModal from '../domains/franquias/LocationsNeighborhoodsModal.vue';
import LocationsSelectionModal from '../domains/franquias/LocationsSelectionModal.vue';
import DevicesFranquiasModal from '../domains/devices_franquias/DevicesFranquiasModal.vue';
import DeviceRegistrationModal from '../domains/devices_franquias/DeviceRegistrationModal.vue';
import InstagramAccountsModal from '../domains/instagram_accounts/InstagramAccountsModal.vue';
import DevicePickerModal from '../domains/messaging_inbox/DevicePickerModal.vue';
import UserModal from '../domains/users/UserModal.vue';
import ChangePasswordModal from '../domains/users/ChangePasswordModal.vue';
import MensagensProgramadasLoteModal from '../domains/mensagens_programadas_lote/MensagensProgramadasLoteModal.vue';
import MessageTemplateSelectionModal from '../domains/mensagens_programadas/MessageTemplateSelectionModal.vue';
import WhatsappMessageTemplatesModal from '../domains/whatsapp_message_templates/WhatsappMessageTemplatesModal.vue';
import PaymentRulesModal from '../domains/payroll/payment-rules/PaymentRulesModal.vue';
import PaymentRulesSummaryModal from '../domains/payroll/payment-rules/PaymentRulesSummaryModal.vue';
import PayrollModal from '../domains/payroll/periods/PayrollModal.vue';
import CreatePeriodModal from '../domains/payroll/periods/CreatePeriodModal.vue';
import PayrollRefundsModal from '../domains/payroll/refunds/PayrollRefundsModal.vue';
import PayrollRefundsDeclineModal from '../domains/payroll/refunds/PayrollRefundsDeclineModal.vue';
import EmployeeAdvancesModal from '../domains/payroll/advances/EmployeeAdvancesModal.vue';
import EmployeeDeductionsModal from '../domains/payroll/deductions/EmployeeDeductionsModal.vue';
import DeductionsModal from '../domains/deductions/DeductionsModal.vue';
import DeductionsListModal from '../domains/deductions/DeductionsListModal.vue';
import OccurrencesModal from '../domains/occurrences/OccurrencesModal.vue';
import ImageViewerModal from '../domains/galeria/ImageViewerModal.vue';
import FiltroModal from '../domains/filtro/FiltroModal.vue';
import PlanoContasModal from '../domains/plano-contas/PlanoContasModal.vue';
import FechamentosModal from '../domains/fechamentos/FechamentosModal.vue';
import TranslationsModal from '../domains/translations/TranslationsModal.vue';
import LanguagesModal from '../domains/languages/LanguagesModal.vue';

// SO (Service Order) Domain Modals
import SOServiceCategoriesModal from '../domains/so-service-categories/SOServiceCategoriesModal.vue';
import SOProductCategoriesModal from '../domains/so-product-categories/SOProductCategoriesModal.vue';
import SOPrioritiesModal from '../domains/so-priorities/SOPrioritiesModal.vue';
import SOItemAttributesModal from '../domains/so-item-attributes/SOItemAttributesModal.vue';
import SOReportAttributesModal from '../domains/so-report-attributes/SOReportAttributesModal.vue';
import SOCatalogFiscalModal from '../domains/so-catalog-fiscal/SOCatalogFiscalModal.vue';
import SOServicesModal from '../domains/so-services/SOServicesModal.vue';
import SOServicePrioritiesModal from '../domains/so-service-priorities/SOServicePrioritiesModal.vue';
import SOAdditionalServicePricesModal from '../domains/so-additional-service-prices/SOAdditionalServicePricesModal.vue';
import SOCombosModal from '../domains/so-combos/SOCombosModal.vue';
import SOComboPricesModal from '../domains/so-combo-prices/SOComboPricesModal.vue';
import SOStockAdjustModal from '../domains/so-stock/SOStockAdjustModal.vue';
import SOStockMovementsModal from '../domains/so-stock/SOStockMovementsModal.vue';
import SOStockCreateModal from '../domains/so-stock/SOStockCreateModal.vue';
import SOAdditionalServicesModal from '../domains/so-additional-services/SOAdditionalServicesModal.vue';
import SOProductionStepsModal from '../domains/so-production-steps/SOProductionStepsModal.vue';
import SOOrderItemDetailModal from '../domains/so-orders/item-detail/SOOrderItemDetailModal.vue';
import SODeliveryDateChangeModal from '../domains/so-orders/SODeliveryDateChangeModal.vue';
import SOReprocessoModal from '../domains/so-orders/SOReprocessoModal.vue';
import SOLaudosComparisonModal from '../domains/so-orders/SOLaudosComparisonModal.vue';
import GlobalSearchModal from '../domains/search/GlobalSearchModal.vue';

// PDV Modals
import PDVClienteWizardModal from '../domains/pdv/wizards/PDVClienteWizardModal.vue';
import PDVServicoWizardModal from '../domains/pdv/wizards/PDVServicoWizardModal.vue';
import PDVProdutosWizardModal from '../domains/pdv/wizards/PDVProdutosWizardModal.vue';
import PDVRegistrarEntregaModal from '../domains/pdv/PDVRegistrarEntregaModal.vue';
import PDVEntregasNotifyModal from '../domains/pdv/PDVEntregasNotifyModal.vue';
import PDVEnviarWhatsAppModal from '../domains/pdv/PDVEnviarWhatsAppModal.vue';
import PDVEntregasDeliveryModal from '../domains/pdv/PDVEntregasDeliveryModal.vue';
import PDVCheckoutModal from '../domains/pdv/PDVCheckoutModal.vue';
import PDVCancelarOSModal from '../domains/pdv/PDVCancelarOSModal.vue';
import PDVVenderComboModal from '../domains/pdv/PDVVenderComboModal.vue';
import ForceOutOfOrderModal from '../domains/pdv/scanner/ForceOutOfOrderModal.vue';
import CaixaModal from '../domains/caixa/CaixaModal.vue';
import CofreModal from '../domains/cofre/CofreModal.vue';
import RedeSituacaoModal from '../domains/rede/RedeSituacaoModal.vue';
import RedeFichaModal from '../domains/rede/RedeFichaModal.vue';
import RedeEncerramentoModal from '../domains/rede/RedeEncerramentoModal.vue';
import RedeEncerramentosModal from '../domains/rede/RedeEncerramentosModal.vue';

// Franchises Subaccounts Modals
import FranchisesSubaccountsWizard from '../domains/subaccounts/franchises-subaccounts/FranchisesSubaccountsWizard.vue';

// Payment Links Modal
import PaymentLinksModal from '../domains/payment-links/PaymentLinksModal.vue';
import PaymentLinksCreateWizard from '../domains/payment-links/PaymentLinksCreateWizard.vue';

// Anticipations Modal
import AnticipationDetailModal from '../domains/anticipations/AnticipationDetailModal.vue';

// Aniversariantes Modals
import AniversariantePreviewModal from '../domains/aniversariantes/AniversariantePreviewModal.vue';
import AniversarianteLoteModal from '../domains/aniversariantes/AniversarianteLoteModal.vue';

// Webhook Logs Modal
import WebhookDetailModal from '../domains/webhook-logs/WebhookDetailModal.vue';

// Agenda/Wizard Modals
import GestaoCompromissoModal from '../domains/agenda/GestaoCompromissoModal.vue';
import AgendaBloqueioModal from '../domains/agenda/AgendaBloqueioModal.vue';
import CheckinWizardModal from '../domains/agenda/CheckinWizardModal.vue';
import WizardServicosModal from '../domains/agenda/wizard-servicos/WizardServicosModal.vue';
import WizardAddServicoModal from '../domains/agenda/wizard-servicos/WizardAddServicoModal.vue';
import AgendaAddCompromissoWizard from '../domains/agenda/AgendaAddCompromissoWizard.vue';
import MensagensProgramadasWizard from '../domains/mensagens_programadas/MensagensProgramadasWizard.vue';
import UserWizard from '../domains/users/UserWizard.vue';
import OrcamentoAddServicoWizard from '../domains/orcamento/OrcamentoAddServicoWizard.vue';
import RecebimentoWizard from '../domains/recebimento/RecebimentoWizard.vue';
import RefundWizard from '../domains/recebimento/RefundWizard.vue';

// Support Ticket Modal
import SupportTicketModal from '../domains/support/SupportTicketModal.vue';
import TicketDetailModal from '../domains/support/TicketDetailModal.vue';

// LGPD Admin Modals
import LgpdTermoModal from '../domains/lgpd/LgpdTermoModal.vue';
import LgpdAceiteDetalheModal from '../domains/lgpd/LgpdAceiteDetalheModal.vue';

// Assistant Sidebar
import AssistantSidebar from '../domains/assistant/AssistantSidebar.vue';
import { config } from '../services/configService';

// Feature `assistente` (backend registry): when off, the drawer and its
// franchise modal are not mounted, so nothing of the assistant fetches.
const assistenteEnabled = config.isFeatureEnabled('assistente');
// Feature `baas` (subaccounts / links / anticipations / invoices / refunds): none of it mounted when off.
const baasEnabled = config.isFeatureEnabled('baas');
import AssistantFranchiseModal from '../domains/assistant/AssistantFranchiseModal.vue';

// Subscription Modals
import SubscribeModal from '../domains/subscription/SubscribeModal.vue';
import SubscriptionDetailModal from '../domains/subscription/SubscriptionDetailModal.vue';

// Pack Purchase Modal (franqueado-facing top-up)
import PackPurchaseModal from '../domains/assistant/packs/PackPurchaseModal.vue';

// Staff Subscription / Credits Modals
import StaffSubscriptionPlanModal from '../domains/staff/subscription/StaffSubscriptionPlanModal.vue';
import StaffSubscriptionsExtendModal from '../domains/staff/subscription/StaffSubscriptionsExtendModal.vue';
import StaffSweepRunsModal from '../domains/staff/subscription/StaffSweepRunsModal.vue';
import StaffCreditsTopupModal from '../domains/staff/credits/StaffCreditsTopupModal.vue';
import StaffCreditsSyncModal from '../domains/staff/credits/StaffCreditsSyncModal.vue';
import CreditTransactionModal from '../domains/staff/credits/CreditTransactionModal.vue';
import CreditPackModal from '../domains/staff/credits/packs/CreditPackModal.vue';
import ManualAdjustmentModal from '../domains/staff/credits/ManualAdjustmentModal.vue';

// Staff Transactions Modal
import StaffTransactionModal from '../domains/staff/transactions/StaffTransactionModal.vue';

// Staff Grace Periods Modals
import GraceGrantModal from '../domains/staff/grace/GraceGrantModal.vue';
import GraceRevokeModal from '../domains/staff/grace/GraceRevokeModal.vue';

// Scheduled Jobs Modal (S10)
import ScheduledJobFormModal from '../domains/staff/scheduling/ScheduledJobFormModal.vue';

// Franchisor Channels Modal (S13)
import ChannelFormModal from '../domains/staff/channels/ChannelFormModal.vue';
import SubscriptionTermModal from '../domains/staff/subscription-terms/SubscriptionTermModal.vue';
import SubscriptionTermAcceptanceModal from '../domains/staff/subscription-terms/SubscriptionTermAcceptanceModal.vue';
import ChannelTemplateModal from '../domains/staff/channels/ChannelTemplateModal.vue';
import TriggerBindingModal from '../domains/staff/channels/TriggerBindingModal.vue';

// Platform events console modals (/config/webhooks)
import PlatformWebhooksModal from '../domains/platform-webhooks/PlatformWebhooksModal.vue';
import PlatformWebhooksTestSendModal from '../domains/platform-webhooks/PlatformWebhooksTestSendModal.vue';

// Manual Reconciliation Modal (#27 follow-up)
import ReconciliationResultModal from '../domains/staff/observability/ReconciliationResultModal.vue';
import BillingThreeSixtyModal from '../domains/staff/observability/BillingThreeSixtyModal.vue';

// Setup Fee Modals
import SetupFeeEmitModal from '../domains/staff/setup-fee/SetupFeeEmitModal.vue'
import SetupFeeMarketModal from '../domains/staff/setup-fee/SetupFeeMarketModal.vue';
import SetupFeeMarkPaidModal from '../domains/staff/setup-fee/SetupFeeMarkPaidModal.vue';
import SetupFeeExemptModal from '../domains/staff/setup-fee/SetupFeeExemptModal.vue';
import SetupFeeRerequireModal from '../domains/staff/setup-fee/SetupFeeRerequireModal.vue';
import PegOverrideModal from '../domains/staff/credits/PegOverrideModal.vue';
import SetupFeeDetailModal from '../domains/staff/setup-fee/SetupFeeDetailModal.vue';


onMounted(() => {

  log('ModalManager', 'onMounted', 'Centralized ModalManager initialized with 85 modals');
});
</script>

<style scoped>
.modal-manager {
  /* Container for modals - modals use fixed positioning so this is just a logical container */
}
</style>
